import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { IoClose } from 'react-icons/io5';
import { Menu, BookOpen, Play, Pause, Volume2, VolumeX, Maximize, RotateCcw } from 'lucide-react';
import ReactPlayer from 'react-player';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../components/tabs';
import Overview from './overview';
import Content from './content';
import Files from './files';
import QuizOverviewModal from '../../content/quizOverview';
import QuizPage from '../../content/quizPage';
import { getFirstVideo } from '../../../shared/utils/courseHelper';
import { useCourseDetails } from '../../../shared/hooks/useCourseDetails';
import { submitVideo } from './actions';
import { Section } from '../../../interfaces/api/courses';

/**
 * Skeleton Loading Component
 */
const SkeletonLoader = ({ className = '' }: { className?: string }) => (
  <div className={`animate-pulse ${className}`}>
    <div className="bg-gray-300 dark:bg-gray-700 rounded"></div>
  </div>
);

/**
 * Video Player Skeleton
 */
const VideoPlayerSkeleton = () => (
  <div className="relative bg-gray-300 dark:bg-gray-700 rounded-2xl overflow-hidden animate-pulse" style={{ aspectRatio: '16/9' }}>
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="w-20 h-20 bg-gray-400 dark:bg-gray-600 rounded-full flex items-center justify-center">
        <div className="w-8 h-8 bg-gray-500 dark:bg-gray-500 rounded-full"></div>
      </div>
    </div>
  </div>
);

/**
 * Content Skeleton for tabs
 */
const ContentSkeleton = () => (
  <div className="space-y-4 animate-pulse">
    <div className="space-y-2">
      <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-3/4"></div>
      <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-1/2"></div>
      <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-5/6"></div>
    </div>
    <div className="space-y-2">
      <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-2/3"></div>
      <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-4/5"></div>
      <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-3/5"></div>
    </div>
    <div className="space-y-2">
      <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-full"></div>
      <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-3/4"></div>
      <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-1/2"></div>
    </div>
  </div>
);

/**
 * Sidebar Content Skeleton
 */
const SidebarSkeleton = () => (
  <div className="p-6 space-y-6 animate-pulse">
    {/* Course sections skeleton */}
    {[1, 2, 3].map((section) => (
      <div key={section} className="space-y-3">
        <div className="h-6 bg-gray-300 dark:bg-gray-700 rounded w-3/4"></div>
        {[1, 2, 3].map((item) => (
          <div key={item} className="flex items-center space-x-3 p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
            <div className="w-8 h-8 bg-gray-300 dark:bg-gray-700 rounded"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-2/3"></div>
              <div className="h-3 bg-gray-300 dark:bg-gray-700 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    ))}
  </div>
);

/**
 * Custom Video Player Component with custom controls
 */
const CustomVideoPlayer = ({ 
  url, 
  thumbnail, 
  onEnded, 
  className = '' 
}: {
  url: string;
  thumbnail: string;
  onEnded: () => void;
  className?: string;
}) => {
  const playerRef = useRef<ReactPlayer>(null);
  const [playing, setPlaying] = useState(false);
  const [played, setPlayed] = useState(0);
  const [loaded, setLoaded] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [muted, setMuted] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [seeking, setSeeking] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const controlsTimeoutRef = useRef<NodeJS.Timeout>();

  // Auto-hide controls
  useEffect(() => {
    const handleMouseMove = () => {
      setShowControls(true);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      if (playing && hasStarted) {
        controlsTimeoutRef.current = setTimeout(() => {
          setShowControls(false);
        }, 3000);
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('mousemove', handleMouseMove);
      container.addEventListener('mouseleave', () => {
        if (playing && hasStarted) {
          setShowControls(false);
        }
      });
    }

    return () => {
      if (container) {
        container.removeEventListener('mousemove', handleMouseMove);
      }
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [playing, hasStarted]);

  // Format time helper
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle play/pause
  const handlePlayPause = () => {
    setPlaying(!playing);
    if (!hasStarted) {
      setHasStarted(true);
    }
  };

  // Handle seek
  const handleSeekChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    setPlayed(value);
    setSeeking(true);
  };

  const handleSeekMouseUp = (e: React.MouseEvent<HTMLInputElement>) => {
    setSeeking(false);
    const value = parseFloat((e.target as HTMLInputElement).value);
    playerRef.current?.seekTo(value);
  };

  // Handle volume
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    setVolume(value);
    setMuted(value === 0);
  };

  // Handle fullscreen
  const handleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Handle replay
  const handleReplay = () => {
    playerRef.current?.seekTo(0);
    setPlayed(0);
    setPlaying(true);
  };

  // Player event handlers
  const handleReady = () => {
    setIsReady(true);
    setIsBuffering(false);
  };

  const handleStart = () => {
    setHasStarted(true);
  };

  const handleProgress = (state: { played: number; loaded: number }) => {
    if (!seeking) {
      setPlayed(state.played);
      setLoaded(state.loaded);
    }
  };

  const handleDuration = (duration: number) => {
    setDuration(duration);
  };

  const handleBuffer = () => {
    setIsBuffering(true);
  };

  const handleBufferEnd = () => {
    setIsBuffering(false);
  };

  const handleEnded = () => {
    setPlaying(false);
    onEnded();
  };

  return (
    <div 
      ref={containerRef}
      className={`relative bg-black rounded-2xl overflow-hidden group ${className}`}
      style={{ aspectRatio: '16/9' }}
    >
      {/* React Player */}
      <ReactPlayer
        ref={playerRef}
        url={url}
        width="100%"
        height="100%"
        playing={playing}
        volume={volume}
        muted={muted}
        light={!hasStarted ? thumbnail : false}
        controls={false}
        onReady={handleReady}
        onStart={handleStart}
        onProgress={handleProgress}
        onDuration={handleDuration}
        onBuffer={handleBuffer}
        onBufferEnd={handleBufferEnd}
        onEnded={handleEnded}
        config={{
          file: {
            attributes: {
              style: {
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }
            }
          }
        }}
      />

      {/* Loading Screen */}
      {(isBuffering || !isReady) && hasStarted && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            <p className="text-white text-sm">Loading...</p>
          </div>
        </div>
      )}

      {/* Play Button Overlay (when not started) */}
      {!hasStarted && (
        <div className="absolute inset-0 flex items-center justify-center">
          <button
            onClick={handlePlayPause}
            className="w-20 h-20 bg-primary bg-opacity-90 hover:bg-opacity-100 rounded-full flex items-center justify-center transition-all duration-200 transform hover:scale-110"
          >
            <Play className="w-8 h-8 text-white ml-1" />
          </button>
        </div>
      )}

      {/* Custom Controls */}
      {hasStarted && (
        <div 
          className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/50 to-transparent p-4 transition-opacity duration-300 ${
            showControls ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {/* Progress Bar */}
          <div className="mb-4">
            <div className="relative h-2 bg-white bg-opacity-30 rounded-lg">
              {/* Buffer indicator */}
              <div 
                className="absolute top-0 left-0 h-full bg-white bg-opacity-50 rounded-lg transition-all duration-300"
                style={{ width: `${loaded * 100}%` }}
              />
              {/* Progress indicator */}
              <div 
                className="absolute top-0 left-0 h-full bg-primary rounded-lg transition-all duration-150"
                style={{ width: `${played * 100}%` }}
              />
              {/* Invisible input for seeking */}
              <input
                type="range"
                min={0}
                max={1}
                step="any"
                value={played}
                onChange={handleSeekChange}
                onMouseUp={handleSeekMouseUp}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              {/* Progress thumb */}
              <div 
                className="absolute top-1/2 transform -translate-y-1/2 w-4 h-4 bg-primary border-2 border-white rounded-full shadow-lg transition-all duration-150"
                style={{ left: `calc(${played * 100}% - 8px)` }}
              />
            </div>
          </div>

          {/* Controls Row */}
          <div className="flex items-center justify-between">
            {/* Left Controls */}
            <div className="flex items-center space-x-4">
              {/* Play/Pause */}
              <button
                onClick={handlePlayPause}
                className="text-white hover:text-primary transition-colors"
              >
                {playing ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
              </button>

              {/* Replay */}
              <button
                onClick={handleReplay}
                className="text-white hover:text-primary transition-colors"
              >
                <RotateCcw className="w-5 h-5" />
              </button>

              {/* Volume */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setMuted(!muted)}
                  className="text-white hover:text-primary transition-colors"
                >
                  {muted || volume === 0 ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                </button>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.1}
                  value={muted ? 0 : volume}
                  onChange={handleVolumeChange}
                  className="w-20 h-1 bg-white bg-opacity-30 rounded-lg appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, rgb(59 130 246) 0%, rgb(59 130 246) ${(muted ? 0 : volume) * 100}%, rgba(255,255,255,0.3) ${(muted ? 0 : volume) * 100}%, rgba(255,255,255,0.3) 100%)`
                  }}
                />
              </div>

              {/* Time */}
              <div className="text-white text-sm">
                {formatTime(played * duration)} / {formatTime(duration)}
              </div>
            </div>

            {/* Right Controls */}
            <div className="flex items-center space-x-4">
              {/* Fullscreen */}
              <button
                onClick={handleFullscreen}
                className="text-white hover:text-primary transition-colors"
              >
                <Maximize className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Slider Styles */}
      <style>{`
        input[type="range"]::-webkit-slider-thumb {
          appearance: none;
          width: 12px;
          height: 12px;
          background: rgb(59 130 246);
          border-radius: 50%;
          cursor: pointer;
          border: 1px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        }
        input[type="range"]::-moz-range-thumb {
          width: 12px;
          height: 12px;
          background: rgb(59 130 246);
          border-radius: 50%;
          cursor: pointer;
          border: 1px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        }
      `}</style>
    </div>
  );
};

/**
 * CourseDetails component displays detailed course information,
 * including video content and tabbed sections for Overview, Content, and Files.
 *
 * @component
 * @returns {React.ReactElement} The rendered CourseDetails component
 */
const CourseDetails: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { courseTitle, id } = useParams<{ courseTitle: string; id: string }>();

  // Use the id parameter for the API call
  const { courseDetails, updateCourseDetails } = useCourseDetails(+(id || 0));

  const [currentVideo, setCurrentVideo] = useState<string | null>(null);
  const [currentThumbnail, setCurrentThumbnail] = useState<string | null>(null);
  const [currentVideoId, setCurrentVideoId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);

  // Modal states - moved from Content component
  const [selectedQuiz, setSelectedQuiz] = useState<any | null>(null);
  const [showQuizPage, setShowQuizPage] = useState(false);
  const [activeQuizData, setActiveQuizData] = useState<any>(null);

  /**
   * Fetches course details and initializes the first video.
   * Sets up the main video player with the first available video.
   */
  useEffect(() => {
    if (courseDetails && !currentVideoId) {
      const mainVideo = getFirstVideo(courseDetails);
      if (mainVideo) {
        setCurrentThumbnail(mainVideo.cover);
        setCurrentVideo(mainVideo.url);
        setCurrentVideoId(mainVideo.id.toString());
      }
    }
  }, [courseDetails, currentVideoId]);

  /**
   * Updates course sections based on video or assessment completion.
   */
  const updateSections = useCallback(
    (sections: Section[], videoId: number | null = null, sectionWithCompletedQuizId: number | null = null) => {
      const updatedSections = sections.map((section, sectionIndex) => {
        // Update videos completion state
        const updatedVideos = section.videos.map((video) => ({
          ...video,
          completed: videoId === video.id || video.completed,
        }));

        // Update quiz completion state
        const updatedAssessment = {
          ...section.assessment,
          completed: sectionWithCompletedQuizId === section.id || section.assessment.completed,
        };

        // Calculate video locks
        const videosWithLocks = updatedVideos.map((video, videoIndex) => {
          // First video of first section is always unlocked
          if (sectionIndex === 0 && videoIndex === 0) {
            return { ...video, isLocked: false };
          }

          // If video is completed, keep it unlocked
          if (video.completed) {
            return { ...video, isLocked: false };
          }

          // For first section's other videos, just check previous videos
          if (sectionIndex === 0) {
            const prevVideosCompleted = updatedVideos.slice(0, videoIndex).every((v) => v.completed);
            return { ...video, isLocked: !prevVideosCompleted };
          }

          // For other sections, check previous section completion
          const prevSection = sections[sectionIndex - 1];
          const isPrevSectionVideosCompleted = prevSection.videos.every((v) => v.completed);
          const isPrevSectionQuizCompleted =
            prevSection.assessment.completed || prevSection.id === sectionWithCompletedQuizId;

          // If previous section is not complete, lock the video
          if (!isPrevSectionVideosCompleted || !isPrevSectionQuizCompleted) {
            return { ...video, isLocked: true };
          }

          // If this is first video of section and prev section complete, unlock it
          if (videoIndex === 0) {
            return { ...video, isLocked: false };
          }

          // For other videos, check if previous videos in current section are completed
          const prevVideosCompleted = updatedVideos.slice(0, videoIndex).every((v) => v.completed);
          return { ...video, isLocked: !prevVideosCompleted };
        });

        // Calculate assessment lock
        const assessmentWithLock = {
          ...updatedAssessment,
          isLocked: !videosWithLocks.every((v) => v.completed),
        };

        return {
          ...section,
          videos: videosWithLocks,
          assessment: assessmentWithLock,
        };
      });

      return updatedSections;
    },
    [],
  );

  // Initialize sections with locks
  useEffect(() => {
    if (courseDetails) {
      const initialSections = updateSections(courseDetails.sections);
      const isFinalAssessmentLocked = initialSections.some(
        (section) => !section.videos.every((v) => v.completed) || !section.assessment.completed,
      );

      updateCourseDetails({
        ...courseDetails,
        sections: initialSections,
        final_assessment: {
          ...courseDetails.final_assessment,
          isLocked: isFinalAssessmentLocked,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseDetails?.id]);

  /**
   * Handles video playback when a new video is selected.
   */
  const handlePlayVideo = useCallback(
    (videoUrl: string, thumbnailUrl: string, videoId: string) => {
      if (videoId === currentVideoId) return;

      setCurrentVideo(videoUrl);
      setCurrentThumbnail(thumbnailUrl);
      setCurrentVideoId(videoId);
      // Close sidebar on mobile when video is selected
      setIsSidebarOpen(false);
    },
    [currentVideoId],
  );

  /**
   * Updates the course progress after a video ends,
   * locking/unlocking videos and assessments as needed.
   */
  const handleVideoEnd = useCallback(async () => {
    if (!currentVideoId || !courseDetails) return;

    try {
      const isSubmitted = await submitVideo(+currentVideoId);
      if (isSubmitted) {
        const updatedSections = updateSections(courseDetails.sections, +currentVideoId);
        const isFinalAssessmentLocked = updatedSections.some(
          (section) => !section.videos.every((v) => v.completed) || !section.assessment.completed,
        );

        updateCourseDetails({
          ...courseDetails,
          sections: updatedSections,
          final_assessment: {
            ...courseDetails.final_assessment,
            isLocked: isFinalAssessmentLocked,
          },
        });
      }
    } catch (error) {
      console.error('Failed to submit video completion:', error);
    }
  }, [currentVideoId, courseDetails, updateSections, updateCourseDetails]);

  /**
   * Unlocks videos and assessments after an assessment is completed.
   */
  const handleAssessmentComplete = async (sectionId: string) => {
    if (!courseDetails) {
      return;
    }
    const updatedSections = updateSections(courseDetails.sections, null, +sectionId);
    const isFinalAssessmentLocked = updatedSections.some(
      (section) => !section.videos.every((v) => v.completed) || !section.assessment.completed,
    );

    updateCourseDetails({
      ...courseDetails,
      sections: updatedSections,
      final_assessment: {
        ...courseDetails.final_assessment,
        isLocked: isFinalAssessmentLocked,
      },
    });
  };

  // Modal handlers - moved from Content component
  const handleNavigateToQuiz = (quizData: { id: string; finalQuiz?: boolean }): void => {
    setSelectedQuiz(quizData);
  };

  const handleCloseQuizModal = () => {
    setSelectedQuiz(null);
  };

  const handleStartQuiz = (quizData: any) => {
    setSelectedQuiz(null);
    setActiveQuizData({
      id: quizData.id,
      questions: quizData.questions,
      onAssessmentComplete: handleAssessmentComplete,
      sectionId: quizData.sectionId,
    });
    setShowQuizPage(true);
  };

  const handleCloseQuiz = () => {
    setShowQuizPage(false);
    setActiveQuizData(null);
  };

  return (
    <div className="flex flex-col h-screen pt-4">
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Main content area - responsive width */}
        <div className="flex flex-col w-2/3 sm:w-full md:w-2/3 p-6 sm:p-3 md:p-6 border-r sm:border-r-0 md:border-r dark:border-gray-800 min-h-0">
          {/* Video Player Section */}
          <div className="mb-6 mx-auto w-full max-w-[800px] flex-shrink-0" style={{ maxHeight: '450px' }}>
            {currentVideo && currentThumbnail ? (
              <CustomVideoPlayer
                url={currentVideo}
                thumbnail={currentThumbnail}
                onEnded={handleVideoEnd}
                className="w-full h-full"
              />
            ) : (
              <VideoPlayerSkeleton />
            )}
          </div>

          {/* Tabs section - Takes remaining space and allows scroll */}
          <div className="flex-1 min-h-0 bg-gray-50 dark:bg-gray-800/50 rounded-2xl flex flex-col">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
              <TabsList className="px-6 sm:px-3 md:px-6 pt-4 flex-shrink-0">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="files">Files</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="flex-1 min-h-0 overflow-auto p-6 sm:p-3 md:p-6">
                {courseDetails ? (
                  <Overview selectedCourse={courseDetails} />
                ) : (
                  <ContentSkeleton />
                )}
              </TabsContent>

              <TabsContent value="files" className="flex-1 min-h-0 overflow-auto p-6 sm:p-3 md:p-6">
                {courseDetails ? (
                  <Files selectedCourse={courseDetails} />
                ) : (
                  <ContentSkeleton />
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>

        {/* Right sidebar - responsive layout with proper scrolling */}
        <div 
          className={`
            relative sm:fixed md:relative 
            top-0 right-0 
            w-1/3 sm:w-full md:w-1/3 
            rounded-md sm:rounded-none
            h-full sm:h-screen md:h-full
            bg-white sm:bg-white md:bg-white lg:bg-transparent dark:bg-gray-900 sm:dark:bg-gray-900 md:dark:bg-gray-900 lg:dark:bg-transparent
            z-10 sm:z-30 md:z-10
            transform transition-transform duration-300 ease-in-out
            ${isSidebarOpen ? 'translate-x-0' : 'translate-x-0 sm:translate-x-full md:translate-x-0'}
            shadow-none sm:shadow-lg md:shadow-none
            flex flex-col
          `}
        >
          {/* Close button for mobile */}
          <button
            onClick={() => setIsSidebarOpen(false)}
            className="absolute top-4 right-4 z-20 p-2 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors hidden sm:block md:hidden flex-shrink-0"
            aria-label="Close sidebar"
          >
            <IoClose className="w-5 h-5" />
          </button>

          {/* Scrollable content area */}
          <div className="flex-1 min-h-0 overflow-auto">
            {courseDetails ? (
              <Content
                selectedCourse={courseDetails as any}
                currentVideoId={currentVideoId as string}
                onPlayVideo={handlePlayVideo}
                onAssessmentComplete={handleAssessmentComplete}
                onNavigateToQuiz={handleNavigateToQuiz}
              />
            ) : (
              <SidebarSkeleton />
            )}
          </div>
        </div>

        {/* Overlay for mobile sidebar */}
        {isSidebarOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-20 md:hidden"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}
      </div>

      {/* Floating Action Button for mobile */}
      <button
        onClick={() => setIsSidebarOpen(true)}
        className="fixed bottom-6 right-[3%] transform -translate-x-1/2 z-40 w-14 h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center !hidden sm:!flex"
        aria-label="Open course content"
      >
        <BookOpen className="w-6 h-6" />
      </button>

      {/* Quiz Modals - Moved outside sidebar to appear over entire viewport */}
      {selectedQuiz && (
        <div className="fixed inset-0 z-50">
          <QuizOverviewModal
            quizId={+selectedQuiz.id}
            finalQuiz={selectedQuiz.finalQuiz}
            onClose={handleCloseQuizModal}
            onStartQuiz={handleStartQuiz}
            onAssessmentComplete={handleAssessmentComplete}
          />
        </div>
      )}

      {/* Quiz Page - Moved outside sidebar to appear over entire viewport */}
      {showQuizPage && activeQuizData && (
        <div className="fixed inset-0 z-50">
          <QuizPage quizData={activeQuizData} onClose={handleCloseQuiz} />
        </div>
      )}
    </div>
  );
};

// Prevent unnecessary re-renders of the entire component
export default React.memo(CourseDetails);